import { TFunction } from 'i18next';
import { ClubAdminAction, ClubAdminActionPayload, ClubAdminChatResponse, ClubAdminModule, ClubAdminView } from './types';

const getResponseText = (data: Pick<ClubAdminChatResponse, 'answer' | 'message' | 'error'>, fallbackText: string) =>
    (data.answer || data.message || data.error || fallbackText).trim();

export const normalizeClubAdminAction = (input: unknown): ClubAdminAction | null => {
    if (!input || typeof input !== 'object') return null;

    const value = input as Record<string, unknown>;
    if (value.action_id !== null && typeof value.action_id !== 'number') return null;
    if (typeof value.confirmation_text !== 'string') return null;

    return {
        type: typeof value.type === 'string' ? value.type : 'action',
        action_id: (typeof value.action_id === 'number' ? value.action_id : null) as number | null,
        confirmation_text: value.confirmation_text,
        payload: ((value.payload as ClubAdminActionPayload) || {}) as ClubAdminActionPayload
    };
};

export const normalizeClubAdminView = (input: unknown): ClubAdminView | null => {
    if (!input || typeof input !== 'object') return null;

    const value = input as Record<string, unknown>;
    if (!['members', 'bookings', 'spaces', 'events', 'invoices', 'statistics', 'allocations'].includes(String(value.type)))
        return null;

    return {
        type: value.type as ClubAdminView['type'],
        items: Array.isArray(value.items)
            ? value.items.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
            : []
    };
};

const normalizeFirstAction = (input: unknown): ClubAdminAction | null => {
    if (!Array.isArray(input)) return null;

    for (const item of input) {
        const normalizedAction = normalizeClubAdminAction(item);
        if (normalizedAction) return normalizedAction;
    }

    return null;
};

const normalizeFirstView = (input: unknown): ClubAdminView | null => {
    if (!Array.isArray(input)) return null;

    for (const item of input) {
        const normalizedView = normalizeClubAdminView(item);
        if (normalizedView) return normalizedView;
    }

    return null;
};

export const normalizeClubAdminChatResponse = (data: ClubAdminChatResponse, fallbackText: string) => ({
    text: getResponseText(data, fallbackText),
    action: normalizeClubAdminAction(data.action) || normalizeFirstAction(data.actions),
    view: normalizeClubAdminView(data.view) || normalizeFirstView(data.views)
});

export const formatDateTime = (value?: string | null) => {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat('de-DE', {
        dateStyle: 'medium',
        timeStyle: 'short'
    }).format(date);
};

const keyLabelKeyMap: Record<string, string> = {
    action_id: 'utils.keys.actionId',
    allocation_id: 'utils.keys.allocationId',
    booking_id: 'utils.keys.bookingId',
    category_id: 'utils.keys.category',
    confirmation_text: 'utils.keys.confirmationText',
    create_member: 'utils.keys.createMember',
    due_date: 'utils.keys.dueDate',
    member_ids: 'utils.keys.memberIds',
    new_end: 'utils.keys.newEnd',
    new_start: 'utils.keys.newStart',
    note: 'utils.keys.note',
    old_end: 'utils.keys.oldEnd',
    old_start: 'utils.keys.oldStart',
    review_note: 'utils.keys.reviewNote',
    space_group_id: 'utils.keys.spaceGroup',
    space_ids: 'utils.keys.spaceIds',
    status: 'utils.keys.status'
};

const actionTypeLabelKeyMap: Record<string, string> = {
    assign_booking_slot: 'utils.actionTypes.assignBookingSlot',
    assign_member_category: 'utils.actionTypes.assignMemberCategory',
    assign_role: 'utils.actionTypes.assignRole',
    assign_space_group: 'utils.actionTypes.assignSpaceGroup',
    contact_request: 'utils.actionTypes.contactRequest',
    create_booking: 'utils.actionTypes.createBooking',
    create_member: 'utils.actionTypes.createMember',
    create_space: 'utils.actionTypes.createSpace',
    notify_booking: 'utils.actionTypes.notifyBooking',
    notify_bookers: 'utils.actionTypes.notifyBookers',
    notify_members: 'utils.actionTypes.notifyMembers',
    reschedule_allocation: 'utils.actionTypes.rescheduleAllocation',
    sync_bookings: 'utils.actionTypes.syncBookings',
    sync_members: 'utils.actionTypes.syncMembers',
    sync_spaces: 'utils.actionTypes.syncSpaces',
    mark_invoice_paid: 'utils.actionTypes.markInvoicePaid',
    payment_reminder: 'utils.actionTypes.paymentReminder'
};

const moduleLabelKeyMap: Partial<Record<ClubAdminModule, string>> = {
    Member: 'module.member',
    Booking: 'module.booking',
    Space: 'module.space',
    Invoice: 'module.invoice'
};

const hiddenPayloadKeys = new Set([
    'action_id',
    'action_required',
    'action_type',
    'confirmation_text'
]);

export const prettifyKey = (value: string, t: TFunction<'ai'>) =>
    (keyLabelKeyMap[value] && t(keyLabelKeyMap[value])) ||
    value
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());

export const getClubAdminActionLabel = (value: string | null | undefined, t: TFunction<'ai'>) => {
    if (!value) return t('utils.actionFallback');
    return (actionTypeLabelKeyMap[value] && t(actionTypeLabelKeyMap[value])) || prettifyKey(value, t);
};

export const getClubAdminModuleLabel = (value: ClubAdminModule, t: TFunction<'ai'>) =>
    (moduleLabelKeyMap[value] && t(moduleLabelKeyMap[value])) || value;

export const getVisibleActionPayloadEntries = (payload: Record<string, unknown>) =>
    Object.entries(payload).filter(([key]) => !hiddenPayloadKeys.has(key));

export const stringifyValue = (value: unknown, t: TFunction<'ai'>): string => {
    if (value === null || value === undefined || value === '') return '–';
    if (typeof value === 'boolean') return value ? t('common:choices.yes') : t('common:choices.no');
    if (Array.isArray(value)) return value.map((item) => stringifyValue(item, t)).join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};
